import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { IAuftraggeber, IAuftraggeberRequest, IDienstleistung, IDienstleistungPreis, IDienstleistungPreisRequest, IMwstSatz, IRechnungsempfaenger, IRolle } from "../types/Types";

/**
 * @author mb
 */
export const StammdatenAPI = createApi({
    reducerPath: "stammdatenAPI",
    baseQuery: fetchBaseQuery(
        {
            baseUrl: process.env.REACT_APP_API_URL! + process.env.REACT_APP_API_VERSION! + "stammdaten",
            credentials: 'include'
        }),
    tagTypes: ['stammdatenAPI'], // for caching
    endpoints: (builder) => ({
        getRollen: builder.mutation<IRolle[], void>(
            {
                query: () => ({
                    url: 'roles/',
                    method: 'get',
                    credentials: 'include'
                })
            }
        ),
        getAuftraggeber: builder.mutation<IAuftraggeber, string>(
            {
                query: (uid) => ({
                    url: 'auftraggeber/' + uid,
                    method: 'get',
                    credentials: 'include'
                })
            }
        ),
        createOrUpdateAuftraggeber: builder.mutation<IAuftraggeber, IAuftraggeberRequest>(
            {
                query: (dto) => ({
                    url: 'auftraggeber/',
                    method: 'put',
                    credentials: 'include',
                    body: dto,
                })
            }
        ),
        deleteAuftraggeber: builder.mutation<void, string>(
            {
                query: (uid) => ({
                    url: 'auftraggeber/' + uid,
                    method: 'delete',
                    credentials: 'include',
                })
            }
        ),
        getRechnungsempfaenger: builder.mutation<IRechnungsempfaenger, string>(
            {
                query: (uid) => ({
                    url: 'rechnungsempfaenger/' + uid,
                    method: 'get',
                    credentials: 'include'
                })
            }
        ),
        getRechnungsempfaengerListe: builder.mutation<IRechnungsempfaenger[], void>(
            {
                query: () => ({
                    url: 'rechnungsempfaenger/',
                    method: 'get',
                    credentials: 'include'
                })
            }
        ),
        createOrUpdateRechnungsempfaenger: builder.mutation<IRechnungsempfaenger, IRechnungsempfaenger>(
            {
                query: (dto) => ({
                    url: 'rechnungsempfaenger/',
                    method: 'put',
                    credentials: 'include',
                    body: dto,
                })
            }
        ),
        deleteRechnungsempfaenger: builder.mutation<void, string>(
            {
                query: (uid) => ({
                    url: 'rechnungsempfaenger/' + uid,
                    method: 'delete',
                    credentials: 'include',
                })
            }
        ),
        getDienstleistung: builder.mutation<IDienstleistung, string>(
            {
                query: (uid) => ({
                    url: 'dienstleistung/' + uid,
                    method: 'get',
                    credentials: 'include'
                })
            }
        ),
        getDienstleistungListe: builder.mutation<IDienstleistung[], void>(
            {
                query: () => ({
                    url: 'dienstleistung/',
                    method: 'get',
                    credentials: 'include'
                })
            }
        ),
        createOrUpdateDienstleistung: builder.mutation<IDienstleistung, IDienstleistung>(
            {
                query: (dto) => ({
                    url: 'dienstleistung/',
                    method: 'put',
                    credentials: 'include',
                    body: dto,
                })
            }
        ),
        deleteDienstleistung: builder.mutation<void, string>(
            {
                query: (uid) => ({
                    url: 'dienstleistung/' + uid,
                    method: 'delete',
                    credentials: 'include',
                })
            }
        ),
        getMwstSatz: builder.mutation<IMwstSatz, string>(
            {
                query: (uid) => ({
                    url: 'mwstsatz/' + uid,
                    method: 'get',
                    credentials: 'include'
                })
            }
        ),
        getMwstSatzListe: builder.mutation<IMwstSatz[], void>(
            {
                query: () => ({
                    url: 'mwstsatz/',
                    method: 'get',
                    credentials: 'include'
                })
            }
        ),
        createOrUpdateMwstSatz: builder.mutation<IMwstSatz, IMwstSatz>(
            {
                query: (dto) => ({
                    url: 'mwstsatz/',
                    method: 'put',
                    credentials: 'include',
                    body: dto,
                })
            }
        ),
        deleteMwstSatz: builder.mutation<void, string>(
            {
                query: (uid) => ({
                    url: 'mwstsatz/' + uid,
                    method: 'delete',
                    credentials: 'include',
                })
            }
        ),
        getDienstleistungPreis: builder.mutation<IDienstleistungPreis, string>(
            {
                query: (uid) => ({
                    url: 'dienstleistung/preis/' + uid,
                    method: 'get',
                    credentials: 'include'
                })
            }
        ),
        getDienstleistungPreisListe: builder.mutation<IDienstleistungPreis[], void>(
            {
                query: () => ({
                    url: 'dienstleistung/preis/',
                    method: 'get',
                    credentials: 'include'
                })
            }
        ),
        createOrUpdateDienstleistungPreis: builder.mutation<IDienstleistungPreis, IDienstleistungPreisRequest>(
            {
                query: (dto) => ({
                    url: 'dienstleistung/preis/',
                    method: 'put',
                    credentials: 'include',
                    body: dto,
                })
            }
        ),
        deleteDienstleistungPreis: builder.mutation<void, string>(
            {
                query: (uid) => ({
                    url: 'dienstleistung/preis/' + uid,
                    method: 'delete',
                    credentials: 'include',
                })
            }
        ),

    })
});

export const {

    useGetRollenMutation,
    useGetAuftraggeberMutation,
    useCreateOrUpdateAuftraggeberMutation,
    useDeleteAuftraggeberMutation,
    useGetRechnungsempfaengerMutation,
    useGetRechnungsempfaengerListeMutation,
    useCreateOrUpdateRechnungsempfaengerMutation,
    useDeleteRechnungsempfaengerMutation,
    useGetDienstleistungMutation,
    useGetDienstleistungListeMutation,
    useCreateOrUpdateDienstleistungMutation,
    useDeleteDienstleistungMutation,
    useGetMwstSatzMutation,
    useGetMwstSatzListeMutation,
    useCreateOrUpdateMwstSatzMutation,
    useDeleteMwstSatzMutation,
    useGetDienstleistungPreisMutation,
    useGetDienstleistungPreisListeMutation,
    useCreateOrUpdateDienstleistungPreisMutation,
    useDeleteDienstleistungPreisMutation,

} = StammdatenAPI;
